// Staff sign-in with Google (OOD-2100). The browser keeps a random secret and gives
// iamauth only its SHA-256 when the sign-in starts. The one-time login code iamauth
// sends back works only together with the secret, so a code that turns up in another
// browser, or in a log, signs nobody in.
const STORAGE_KEY = "phoenix.ssoVerifier";
// iamauth gives up on a sign-in after 15 minutes, so an older secret matches nothing.
const MAX_AGE_MS = 15 * 60 * 1000;
const VERIFIER_FORMAT = /^[A-Za-z0-9_-]{43}$/;
// A stored time slightly ahead of the clock is a clock correction; further ahead it is not a real time.
const CLOCK_SLACK_MS = 60 * 1000;

const toBase64Url = (bytes) =>
  window
    .btoa(String.fromCharCode.apply(null, bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

const toBytes = (text) => {
  const bytes = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i++) bytes[i] = text.charCodeAt(i);
  return bytes;
};

// Resolves to { verifier, challenge }, or to null in a browser that cannot make them.
export const createSsoVerifier = () => {
  try {
    const { crypto } = window;
    const verifier = toBase64Url(crypto.getRandomValues(new Uint8Array(32)));
    return crypto.subtle
      .digest("SHA-256", toBytes(verifier))
      .then((hash) => ({
        verifier,
        challenge: toBase64Url(new Uint8Array(hash)),
      }))
      .catch(() => null);
  } catch (e) {
    return Promise.resolve(null);
  }
};

export const rememberSsoVerifier = (verifier) => {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ verifier, at: Date.now() })
    );
  } catch (e) {
    // Without storage the return cannot be completed, and the page says so then.
  }
};

// Good for one return: whatever is stored is removed, usable or not.
export const takeSsoVerifier = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    window.localStorage.removeItem(STORAGE_KEY);
    const age = stored ? Date.now() - stored.at : NaN;
    if (
      !stored ||
      typeof stored.verifier !== "string" ||
      !VERIFIER_FORMAT.test(stored.verifier) ||
      typeof stored.at !== "number" ||
      !(age > -CLOCK_SLACK_MS && age <= MAX_AGE_MS)
    ) {
      return null;
    }
    return stored.verifier;
  } catch (e) {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (ignored) {
      // storage is not usable at all
    }
    return null;
  }
};
